'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { normalizeChainedHashFragment } from '@/_lib/next-link-href'

const STORAGE_VERSION = 'v1'
const SCROLL_THROTTLE_MS = 500
const SCROLL_END_DEBOUNCE_MS = 150
const RESTORE_DELAY_MS = 1

export function scrollPersistenceStorageKey(pathname: string): string {
	return `medante:scrollY:${STORAGE_VERSION}:${pathname}`
}

export function clearSavedScrollY(pathname: string): void {
	try {
		sessionStorage.removeItem(scrollPersistenceStorageKey(pathname))
	} catch {
		// ignore
	}
}

function readSavedScrollY(pathname: string): number | null {
	try {
		const raw = sessionStorage.getItem(scrollPersistenceStorageKey(pathname))
		if (raw == null) return null
		const y = Number(raw)
		return Number.isFinite(y) && y >= 0 ? y : null
	} catch {
		return null
	}
}

function writeSavedScrollY(pathname: string, y: number): void {
	try {
		sessionStorage.setItem(
			scrollPersistenceStorageKey(pathname),
			String(Math.max(0, Math.round(y)))
		)
	} catch {
		// ignore quota / private mode
	}
}

function significantScrollDelta(a: number, b: number): boolean {
	const threshold = Math.max(72, Math.round(window.innerHeight * 0.08))
	return Math.abs(a - b) > threshold
}

function clampScrollTop(y: number): number {
	const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
	return Math.min(max, Math.max(0, y))
}

/** Route-level `loading.tsx` overlay — inline order widgets use `max-h-*` on the same root. */
function isRouteLoadingOverlayRoot(el: HTMLElement): boolean {
	if (el.getAttribute('aria-label') !== 'Načítavam...' || el.getAttribute('role') !== 'status') {
		return false
	}
	const cls = typeof el.className === 'string' ? el.className : ''
	return !cls.includes('max-h-')
}

function findRouteLoadingInRemovedSubtree(root: Node): HTMLElement | null {
	if (root instanceof HTMLElement) {
		if (isRouteLoadingOverlayRoot(root)) return root
		const nested = root.querySelectorAll('[aria-label="Načítavam..."]')
		for (const el of nested) {
			if (el instanceof HTMLElement && isRouteLoadingOverlayRoot(el)) return el
		}
	}
	return null
}

/**
 * Persists vertical scroll in sessionStorage (per pathname) while scrolling,
 * and restores it when the document scroll position drifts after a full reload
 * or after the App Router route `loading.tsx` overlay is removed.
 */
export function ScrollPositionPersistence() {
	const pathname = usePathname()
	const pathnameRef = useRef(pathname)
	pathnameRef.current = pathname

	useLayoutEffect(() => {
		if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
			history.scrollRestoration = 'manual'
		}
	}, [])

	useEffect(() => {
		let lastThrottleWrite = 0
		let debounceId: ReturnType<typeof setTimeout> | null = null

		const persist = () => {
			writeSavedScrollY(pathnameRef.current, window.scrollY)
		}

		const onScroll = () => {
			const now = Date.now()
			if (now - lastThrottleWrite >= SCROLL_THROTTLE_MS) {
				lastThrottleWrite = now
				persist()
			}
			if (debounceId) clearTimeout(debounceId)
			debounceId = setTimeout(() => {
				debounceId = null
				persist()
			}, SCROLL_END_DEBOUNCE_MS)
		}

		const onScrollEnd = () => {
			if (debounceId) clearTimeout(debounceId)
			debounceId = null
			persist()
		}

		window.addEventListener('scroll', onScroll, { passive: true })
		window.addEventListener('scrollend', onScrollEnd, { passive: true })

		return () => {
			window.removeEventListener('scroll', onScroll)
			window.removeEventListener('scrollend', onScrollEnd)
			if (debounceId) clearTimeout(debounceId)
		}
	}, [])

	useEffect(() => {
		const runRestoreCheck = () => {
			const keyPath = pathnameRef.current
			const saved = readSavedScrollY(keyPath)
			const urlHash =
				typeof window !== 'undefined'
					? normalizeChainedHashFragment(window.location.hash.replace(/^#/, ''))
					: ''
			const current = window.scrollY
			/** Hash URLs are handled by `ScrollToTopOnNavigate`; restoring saved Y fights fragment scroll on reload. */
			if (urlHash) return
			if (saved == null) return
			const y = clampScrollTop(saved)
			if (significantScrollDelta(current, y)) {
				window.scrollTo({ top: y, left: 0, behavior: 'instant' })
			}
		}

		const scheduleRestoreCheck = () => {
			window.setTimeout(runRestoreCheck, RESTORE_DELAY_MS)
		}

		const nav = performance.getEntriesByType('navigation')[0] as
			| PerformanceNavigationTiming
			| undefined
		const initialNavReloadOrBack = nav?.type === 'reload' || nav?.type === 'back_forward'

		const onLoad = () => {
			if (initialNavReloadOrBack) scheduleRestoreCheck()
		}
		window.addEventListener('load', onLoad, { once: true })

		const onPageShow = (e: PageTransitionEvent) => {
			if (e.persisted) scheduleRestoreCheck()
		}
		window.addEventListener('pageshow', onPageShow)

		const mo = new MutationObserver(mutations => {
			for (const m of mutations) {
				for (const n of m.removedNodes) {
					if (findRouteLoadingInRemovedSubtree(n)) {
						scheduleRestoreCheck()
						return
					}
				}
			}
		})
		mo.observe(document.body, { childList: true, subtree: true })

		// Client navigations (incl. browser back/forward): after other effects clear storage on push, restore if still saved.
		scheduleRestoreCheck()

		return () => {
			window.removeEventListener('load', onLoad)
			window.removeEventListener('pageshow', onPageShow)
			mo.disconnect()
		}
	}, [pathname])

	return null
}
